/**
 * Project tabs: every open project runs as its own full copy of the app in a
 * same-origin <iframe>, so the singleton stores (workspace, scene, model,
 * play, agents, ...) stay isolated per project without any refactor. The outer
 * window is the "tab host"; each frame is "embedded".
 *
 * This module holds the URL contract and the postMessage protocol between the
 * two. Every message is tagged and only accepted from our own origin.
 */

export const TAB_MESSAGE_TAG = 'ai-studio.tab';

/** Frame -> host. */
export type FrameMessage =
  /** Current tab label and unsaved state. */
  | { type: 'state'; name: string; dirty: boolean }
  /** The user asked to close this project (menu, palette). */
  | { type: 'close' }
  /** Answer to a host `prepare-close`: pending saves are flushed. */
  | { type: 'close-ready'; dirty: boolean }
  /** Ctrl+Tab and friends pressed while focus sat inside the frame. */
  | { type: 'cycle'; delta: 1 | -1 }
  /** The frame's session is gone; the host should re-check and show login. */
  | { type: 'auth-lost' }
  /** How many items in this project wait on the user (generation approvals / reviews); 0 clears. */
  | { type: 'attention'; count: number };

/** Host -> frame. */
export type HostMessage =
  /** Flush pending saves, then reply `close-ready`. */
  | { type: 'prepare-close' }
  /** A native menu command the frame's workspace should run. */
  | { type: 'menu'; command: string }
  /** The tab became visible. */
  | { type: 'activated' }
  /** Another tab (or the home page) is now on screen. */
  | { type: 'deactivated' };

type Tagged<T> = T & { tag: typeof TAB_MESSAGE_TAG };

export interface EmbedParams {
  embedded: boolean;
  projectId: number | null;
}

/** Parse `?embedded=1&project=<id>`; anything malformed means "not embedded". */
export function parseEmbedParams(search: string): EmbedParams {
  const params = new URLSearchParams(search);
  if (params.get('embedded') !== '1') return { embedded: false, projectId: null };
  const raw = params.get('project') ?? '';
  const id = /^\d+$/.test(raw) ? Number(raw) : NaN;
  return { embedded: true, projectId: Number.isSafeInteger(id) && id > 0 ? id : null };
}

let cached: EmbedParams | null = null;

/** This window's embed parameters (read once). */
export function embedParams(): EmbedParams {
  if (cached) return cached;
  cached =
    typeof window === 'undefined'
      ? { embedded: false, projectId: null }
      : parseEmbedParams(window.location.search);
  // Only meaningful when there really is a host window above us.
  if (cached.embedded && typeof window !== 'undefined' && window.parent === window) {
    cached = { embedded: false, projectId: null };
  }
  return cached;
}

export function isEmbedded(): boolean {
  return embedParams().embedded;
}

/** URL a tab's iframe loads. */
export function frameUrl(baseUrl: string, projectId: number): string {
  const base = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  return `${base}?project=${projectId}&embedded=1`;
}

/** Validate an incoming message: our origin, our tag, a known shape. */
export function readTabMessage<T extends FrameMessage | HostMessage>(
  event: MessageEvent,
  origin: string,
): T | null {
  if (event.origin !== origin) return null;
  const data = event.data as unknown;
  if (!data || typeof data !== 'object') return null;
  const record = data as { tag?: unknown; type?: unknown };
  if (record.tag !== TAB_MESSAGE_TAG || typeof record.type !== 'string') return null;
  return data as T;
}

/** A waiting-items count from a frame: a non-negative whole number, anything else is 0. */
export function normalizeAttention(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.min(999, Math.floor(value)) : 0;
}

/** Frame -> host. No-op outside embedded mode. */
export function postToHost(message: FrameMessage): void {
  if (!isEmbedded()) return;
  const tagged: Tagged<FrameMessage> = { ...message, tag: TAB_MESSAGE_TAG };
  window.parent.postMessage(tagged, window.location.origin);
}

/** Host -> one frame. */
export function postToFrame(
  frame: HTMLIFrameElement | null | undefined,
  message: HostMessage,
): void {
  const target = frame?.contentWindow;
  if (!target) return;
  const tagged: Tagged<HostMessage> = { ...message, tag: TAB_MESSAGE_TAG };
  target.postMessage(tagged, window.location.origin);
}

/** Tab-cycling chords, shared by host and frames. Returns the direction or 0. */
export function tabCycleDelta(
  event: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'altKey'>,
): 1 | -1 | 0 {
  if (!event.ctrlKey || event.altKey || event.metaKey) return 0;
  if (event.key === 'Tab') return event.shiftKey ? -1 : 1;
  if (event.shiftKey) return 0;
  if (event.key === 'PageDown') return 1;
  if (event.key === 'PageUp') return -1;
  return 0;
}
